import type { Canvas } from '@/hooks/useCanvas'
import type { Node, Edge } from '@xyflow/react'

type Viewport = { x: number; y: number; zoom: number }

/**
 * Deep comparison utilities for canvas state
 * Used to detect meaningful changes and avoid unnecessary saves
 */

/**
 * Check if two arrays are deeply equal
 */
function arraysEqual<T>(a: T[], b: T[]): boolean {
  if (a.length !== b.length) return false

  for (let i = 0; i < a.length; i++) {
    if (JSON.stringify(a[i]) !== JSON.stringify(b[i])) {
      return false
    }
  }

  return true
}

/**
 * Check if viewport has changed meaningfully
 * Ignores tiny floating point differences
 */
export function hasViewportChanged(prev: Viewport, next: Viewport): boolean {
  const threshold = 0.001 // Ignore sub-pixel differences

  return (
    Math.abs(prev.x - next.x) > threshold ||
    Math.abs(prev.y - next.y) > threshold ||
    Math.abs(prev.zoom - next.zoom) > threshold
  )
}

/**
 * Check if nodes have changed
 */
export function hasNodesChanged(prev: Node[], next: Node[]): boolean {
  return !arraysEqual(prev, next)
}

/**
 * Check if edges have changed
 */
export function hasEdgesChanged(prev: Edge[], next: Edge[]): boolean {
  return !arraysEqual(prev, next)
}

/**
 * Check if canvas config has changed
 */
export function hasConfigChanged(prev: Record<string, unknown>, next: Record<string, unknown>): boolean {
  return JSON.stringify(prev) !== JSON.stringify(next)
}

/**
 * Check if canvas has any meaningful changes
 */
export function hasCanvasChanged(
  prev: {
    title?: string
    nodes?: Node[]
    edges?: Edge[]
    viewport?: Viewport
    config?: Record<string, unknown>
  },
  next: {
    title?: string
    nodes?: Node[]
    edges?: Edge[]
    viewport?: Viewport
    config?: Record<string, unknown>
  }
): boolean {
  // Title change
  if (prev.title !== next.title) return true

  // Nodes change
  if (prev.nodes && next.nodes && hasNodesChanged(prev.nodes, next.nodes)) {
    return true
  }

  // Edges change
  if (prev.edges && next.edges && hasEdgesChanged(prev.edges, next.edges)) {
    return true
  }

  // Viewport change
  if (prev.viewport && next.viewport && hasViewportChanged(prev.viewport, next.viewport)) {
    return true
  }

  // Config change
  if (prev.config && next.config && hasConfigChanged(prev.config, next.config)) {
    return true
  }

  return false
}

/**
 * Get only the changed fields between two canvas states
 * Returns null if no meaningful changes detected
 */
export function getCanvasChanges(
  prev: {
    title?: string
    nodes?: Node[]
    edges?: Edge[]
    viewport?: Viewport
    config?: Record<string, unknown>
  },
  next: {
    title?: string
    nodes?: Node[]
    edges?: Edge[]
    viewport?: Viewport
    config?: Record<string, unknown>
  }
): Partial<{
  title: string
  nodes: Node[]
  edges: Edge[]
  config: Record<string, unknown>
}> | null {
  const changes: Partial<{
    title: string
    nodes: Node[]
    edges: Edge[]
    config: Record<string, unknown>
  }> = {}
  let hasChanges = false

  // Title change
  if (next.title !== undefined && prev.title !== next.title) {
    changes.title = next.title
    hasChanges = true
  }

  // Nodes change
  if (next.nodes !== undefined) {
    if (!prev.nodes || hasNodesChanged(prev.nodes, next.nodes)) {
      changes.nodes = next.nodes
      hasChanges = true
    }
  }

  // Edges change
  if (next.edges !== undefined) {
    if (!prev.edges || hasEdgesChanged(prev.edges, next.edges)) {
      changes.edges = next.edges
      hasChanges = true
    }
  }

  // Viewport change (store in config)
  if (next.viewport !== undefined) {
    if (!prev.viewport || hasViewportChanged(prev.viewport, next.viewport)) {
      changes.config = {
        ...(prev.config || {}),
        viewport: next.viewport
      }
      hasChanges = true
    }
  }

  // Config change (non-viewport) — merge with any viewport config already set
  if (next.config !== undefined) {
    if (!prev.config || hasConfigChanged(prev.config, next.config)) {
      changes.config = { ...(changes.config || {}), ...next.config }
      hasChanges = true
    }
  }

  return hasChanges ? changes : null
}
